<template>
    <div class="v-image"
         :class="{ 'v-image--loaded': loaded, 'v-image--instant': instant }"
         :style="radius ? { borderRadius: radius } : null">
        <span v-if="!loaded"
              class="v-image__skeleton"
              aria-hidden="true"></span>
        <img ref="img"
             class="v-image__img"
             :src="src"
             :alt="alt"
             :loading="loading"
             :decoding="decoding"
             @load="onLoad"
             @error="onError">
    </div>
</template>

<script>
export default {
    // Wraps a dish/product <img> with a shimmering skeleton placeholder and a
    // fade-in on load, so large menu photos no longer pop in over a blank box.
    // Fills its parent (which sets the size and, where needed, the clip radius).
    name: "VImage",
    props: {
        src: { type: String, required: true },
        alt: { type: String, default: "" },
        // Applied to the wrapper when the parent doesn't already clip the image
        // (e.g. the menu list thumb, whose wrap has no overflow:hidden).
        radius: { type: String, default: "" },
        // Native lazy-loading by default: in a long menu the browser only fetches
        // photos as they approach the viewport instead of all at once.
        loading: { type: String, default: "lazy" },
        decoding: { type: String, default: "async" },
    },
    data() {
        return {
            loaded: false,
            // Skip the fade when the image is served from cache (see mounted).
            instant: false,
        };
    },
    mounted() {
        // A fully-cached <img> can finish before this listener attaches and never
        // fire `load`, which would leave the skeleton shimmering forever. Detect
        // that here and reveal it immediately — without the fade, so a warmed
        // image (e.g. the detail sheet reusing a card's photo) feels instant.
        const img = this.$refs.img;
        if (img && img.complete && img.naturalWidth > 0) {
            this.instant = true;
            this.loaded = true;
        }
    },
    methods: {
        onLoad() {
            this.loaded = true;
        },
        onError() {
            // Drop the skeleton on error too, so a broken URL doesn't shimmer
            // forever — the browser's own broken-image state shows instead.
            this.loaded = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.v-image {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &__skeleton {
        position: absolute;
        inset: 0;

        background-color: var(--surface-subtle);
        background-image: linear-gradient(
            100deg,
            transparent 25%,
            var(--v-image-shimmer) 50%,
            transparent 75%
        );
        background-size: 220% 100%;
        background-repeat: no-repeat;

        --v-image-shimmer: rgba(255, 255, 255, 0.55);

        animation: v-image-shimmer 1.25s ease-in-out infinite;

        :root[data-theme="dark"] & {
            --v-image-shimmer: rgba(255, 255, 255, 0.06);
        }
    }

    &__img {
        width: 100%;
        height: 100%;
        display: block;

        object-fit: cover;

        opacity: 0;
        transition: opacity var(--dur-base) var(--ease-standard);
    }

    &--loaded &__img {
        opacity: 1;
    }

    &--instant &__img {
        transition: none;
    }
}

@keyframes v-image-shimmer {
    from { background-position: 220% 0; }
    to   { background-position: -120% 0; }
}

@media (prefers-reduced-motion: reduce) {
    .v-image__skeleton {
        animation: none;
    }

    .v-image__img {
        transition: none;
    }
}
</style>
